import React, { useState, useEffect, useMemo } from 'react';
import { Joyride, EventData, STATUS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';

import { useUpdateTourStatusMutation } from '@/features/admin/api/adminApi';
import { CustomTooltip } from './CustomTooltip';
import { getOrgAdminSteps, getMemberSteps, CustomStep } from '../config/tourSteps';
import { getTourStyles, getTourOptions } from '../config/tourStyles';
import { UserAdminInfo } from '../types/onboarding.types';

export const OnboardingTour: React.FC = () => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const adminUserRaw = localStorage.getItem('admin_user');
  const adminUser: UserAdminInfo | null = adminUserRaw ? JSON.parse(adminUserRaw) : null;
  const isOrgAdmin = adminUser?.role === 'Admin' || adminUser?.role === 'SuperAdmin' || adminUser?.orgRole === 'admin';
  
  const [updateTourStatus] = useUpdateTourStatusMutation();

  // Auto-start tour if user has not seen it yet
  useEffect(() => {
    if (adminUser?.role === 'SuperAdmin') return;

    const hasSeenTour = adminUser?.hasSeenTour || adminUser?.has_seen_tour;

    if (!hasSeenTour) {
      if (location.pathname !== '/admin/dashboard') {
        navigate('/admin/dashboard');
      }
      const timer = setTimeout(() => {
        setRun(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [adminUser?.id, adminUser?.role]);

  // Listen for custom trigger event to restart tour from settings / header help button
  useEffect(() => {
    const handleStartTour = () => {
      setRun(false);
      setStepIndex(0);
      if (location.pathname !== '/admin/dashboard') {
        navigate('/admin/dashboard');
      }
      setTimeout(() => {
        setRun(true);
      }, 500);
    };

    window.addEventListener('start-finn-tour', handleStartTour);
    return () => window.removeEventListener('start-finn-tour', handleStartTour);
  }, [location.pathname, navigate]);

  // Generate steps based on user role
  const steps: CustomStep[] = useMemo(() => {
    const sharkName = adminUser?.sharkName || 'Finn';
    const tourSteps = isOrgAdmin ? getOrgAdminSteps(sharkName) : getMemberSteps(sharkName);

    return tourSteps.map(step => ({ ...step, disableOverlayClose: true }));
  }, [isOrgAdmin, adminUser?.sharkName]);

  // Navigate to module page when current step specifies a route
  useEffect(() => {
    if (run && steps[stepIndex]?.data?.route) {
      const targetRoute = steps[stepIndex].data.route;
      if (location.pathname !== targetRoute) {
        navigate(targetRoute);
      }
    }
  }, [stepIndex, run, steps, location.pathname, navigate]);

  // Handle tour progression, step navigation & completion
  const handleJoyrideCallback = async (data: EventData) => {
    const { status, type, action, index } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status as any)) {
      setRun(false);
      
      const hasSeenDb = adminUser?.hasSeenTour || adminUser?.has_seen_tour;
      
      if (!hasSeenDb) {
        try {
          await updateTourStatus({ hasSeenTour: true }).unwrap();
          if (adminUser) {
            adminUser.hasSeenTour = true;
            localStorage.setItem('admin_user', JSON.stringify(adminUser));
          }
        } catch (err) {
          console.error("Failed to save tour status to database", err);
        }
      }

      navigate('/admin/dashboard');
      return;
    }

    if (type === 'step:after' || type === 'error:target_not_found') {
      const nextIndex = index + (action === 'prev' ? -1 : 1);
      setStepIndex(nextIndex);
    }
  };

  if (adminUser?.role === 'SuperAdmin') {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous={true}
      tooltipComponent={CustomTooltip}
      options={getTourOptions()}
      scrollToFirstStep={true}
      floatingOptions={{
        strategy: 'fixed',
        shiftOptions: { padding: 16 },
        flipOptions: {
          padding: 24,
          fallbackPlacements: ['bottom-start', 'bottom', 'right-start', 'left-start', 'top-start', 'top'],
        },
      }}
      styles={getTourStyles()}
      onEvent={handleJoyrideCallback}
    />
  );
};
